import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import React from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { IBlogImportSystemJob, useBlogImportHistoryQuery } from '../../../../queries/Blog/useBlogImportHistoryQuery';
import { useBlogImportRevertMutation } from '../../../../queries/Blog/useBlogImportRevertMutation';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components/Checkbox';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../../web/components/DataGrid';
import { Databoard, DataboardBody, DataboardFilters, DataboardHeader } from '../../../../web/components/Databoard';
import { DeleteConfirmation } from '../../../../web/components/DeleteConfirmation';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../web/components/Tooltip';
import { Badge } from '../../../../web/components/dataBoards/Badge';
import { InfoIcon } from '../../../../web/components/svgs/icons/InfoIcon';
import { brandSecondary, charmGray, destructive, success } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface BlogImportHistoryProps {
	accountId: string;
}

export const BlogImportHistory: React.FC<BlogImportHistoryProps> = ({ accountId }) => {
	const errorMessages = useErrorMessages();
	const [showReverted, setShowReverted] = React.useState(false);
	const [jobToRevert, setJobToRevert] = React.useState<IBlogImportSystemJob | null>(null);
	const [isRevertModalOpen, setIsRevertModalOpen] = React.useState(false);

	const {
		data: imports,
		isPending,
		refetch,
	} = useBlogImportHistoryQuery({
		accountId,
		showReverted,
		meta: {
			toastErrorMessage: 'Error fetching blog import history',
			errorLogging: {
				category: 'BlogImportHistory',
				action: 'GetBlogImportHistoryQuery-Error',
			},
		},
	});

	const revertMutation = useBlogImportRevertMutation({
		onSuccess: () => {
			toast.success('Blog import reverted successfully');
			setJobToRevert(null);
			refetch();
		},
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const handleRevertClick = (job: IBlogImportSystemJob) => {
		setJobToRevert(job);
		setIsRevertModalOpen(true);
	};

	const handleRevertConfirmation = (shouldRevert: boolean) => {
		if (shouldRevert && jobToRevert) {
			revertMutation.mutate({
				systemJobId: jobToRevert.id,
				accountId,
			});
		}
		setIsRevertModalOpen(false);
		setJobToRevert(null);
	};

	const getStatusBadge = (job: IBlogImportSystemJob) => {
		if (job.isReverted) {
			return <Badge foregroundColor={destructive} text='Reverted' />;
		}
		switch (job.status) {
			case 'Success':
				return <Badge foregroundColor={success} text='Success' />;
			case 'Failed':
				return (
					<div className={css(baseStyleSheet.horizontalStack)}>
						<Badge foregroundColor={destructive} text='Failed' />
						{job.additionalInfo && (
							<Tooltip>
								<TooltipTrigger asChild>
									<span>
										<InfoIcon fillColor={destructive} className={css(baseStyleSheet.ml1)} />
									</span>
								</TooltipTrigger>
								<TooltipContent>{job.additionalInfo}</TooltipContent>
							</Tooltip>
						)}
					</div>
				);
			case 'InProgress':
				return <Badge foregroundColor={brandSecondary} text='In Progress' />;
			default:
				return <Badge foregroundColor={charmGray} text={job.status || 'Unknown'} />;
		}
	};

	const formatDate = (date: Date | string) => {
		if (!date) return '-';
		return moment(date).format('MMM DD, YYYY h:mm A');
	};

	const formatUserName = (job: IBlogImportSystemJob) => {
		if (job.userReference) {
			const { firstName, lastName } = job.userReference;
			if (firstName && lastName) {
				return `${firstName} ${lastName}`;
			}
			if (firstName) {
				return firstName;
			}
			if (lastName) {
				return lastName;
			}
		}
		// Fallback to userId if no userReference is available
		return job.userId || '-';
	};

	const renderSource = (job: IBlogImportSystemJob) => {
		if (job.rssUrl) {
			return (
				<a
					href={job.rssUrl}
					target='_blank'
					rel='noopener noreferrer'
					className={css(styleSheet.sourceLink)}
					title={job.rssUrl}
				>
					{job.rssUrl}
				</a>
			);
		}

		return job.importSource || 'RSS/File';
	};

	const formatPostCount = (job: IBlogImportSystemJob) => {
		const importDate = job.dateCreatedUtc ? new Date(job.dateCreatedUtc) : null;
		const cutoffDate = new Date('2025-09-01T00:00:00Z');

		// If the import was before September 1st, 2025, show "-" since we don't have that data
		if (!importDate || importDate < cutoffDate) {
			return '-';
		}

		return job.importedPostCount || job.recordsSucceeded || 0;
	};

	if (isPending) {
		return (
			<div className={css(baseStyleSheet.flexCenter, styleSheet.loadingContainer)}>
				<LoadingSpinner />
			</div>
		);
	}

	return (
		<TooltipProvider>
			<Databoard>
				<DataboardHeader title='Blog Import History' />

				<DataboardFilters>
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.filterContainer)}>
						<Checkbox
							id='include-reverted'
							checked={showReverted}
							onChange={e => setShowReverted((e.target as HTMLInputElement).checked)}
						/>
						<label htmlFor='include-reverted'>Show reverted imports</label>
					</div>
				</DataboardFilters>

				<DataboardBody>
					{!imports || imports.length === 0 ? (
						<div className={css(baseStyleSheet.flexCenter, styleSheet.noResultsContainer)}>
							<p>No blog imports have been performed yet.</p>
						</div>
					) : (
						<DataGrid>
							<DataGridColumns>
								<DataGridColumn>Import Date</DataGridColumn>
								<DataGridColumn>Source</DataGridColumn>
								<DataGridColumn>Posts Imported</DataGridColumn>
								<DataGridColumn>Status</DataGridColumn>
								<DataGridColumn>Imported By</DataGridColumn>
								<DataGridColumn>Actions</DataGridColumn>
							</DataGridColumns>
							<DataGridRows>
								{imports.map(job => (
									<DataGridRow key={job.id}>
										<DataGridRowItem>{formatDate(job.dateCreatedUtc)}</DataGridRowItem>
										<DataGridRowItem>{renderSource(job)}</DataGridRowItem>
										<DataGridRowItem>{formatPostCount(job)}</DataGridRowItem>
										<DataGridRowItem>{getStatusBadge(job)}</DataGridRowItem>
										<DataGridRowItem>{formatUserName(job)}</DataGridRowItem>
										<DataGridRowItem>
											{job.status === 'Success' && !job.isReverted ? (
												<Button
													onClick={() => handleRevertClick(job)}
													variant='secondary'
													size='sm'
													disabled={revertMutation.isPending}
												>
													Revert
												</Button>
											) : (
												<div className={css(styleSheet.actionPlaceholder)} />
											)}
										</DataGridRowItem>
									</DataGridRow>
								))}
							</DataGridRows>
						</DataGrid>
					)}
				</DataboardBody>
			</Databoard>

			<DeleteConfirmation
				isOpen={isRevertModalOpen}
				onFinish={handleRevertConfirmation}
				title='Revert Blog Import'
				bodyText={
					<div>
						<p>
							Are you sure you want to revert this blog import? This will remove{' '}
							{jobToRevert?.importedPostCount || jobToRevert?.recordsSucceeded || 0} imported posts.
						</p>
						<p className={css(styleSheet.warningText)}>This action cannot be undone.</p>
					</div>
				}
				destructiveButtonText='Revert Import'
				showSpinner={revertMutation.isPending}
			/>
		</TooltipProvider>
	);
};
